// Stable color assignment for each doctor.
// A doctor may have an explicit `colorTag` (palette name OR hex). If not set,
// we hash their userId to pick a deterministic palette slot.

export const DOCTOR_PALETTE = [
  { name: 'orange', dot: '#f97316', bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
  { name: 'green',  dot: '#22c55e', bg: '#dcfce7', border: '#86efac', text: '#166534' },
  { name: 'amber',  dot: '#f59e0b', bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  { name: 'cyan',   dot: '#06b6d4', bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
  { name: 'purple', dot: '#a855f7', bg: '#f3e8ff', border: '#d8b4fe', text: '#6b21a8' },
  { name: 'pink',   dot: '#ec4899', bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
  { name: 'lime',   dot: '#84cc16', bg: '#ecfccb', border: '#bef264', text: '#3f6212' },
  { name: 'violet', dot: '#8b5cf6', bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  { name: 'rose',   dot: '#f43f5e', bg: '#ffe4e6', border: '#fda4af', text: '#9f1239' },
  { name: 'sky',    dot: '#0ea5e9', bg: '#e0f2fe', border: '#7dd3fc', text: '#075985' },
  { name: 'teal',   dot: '#14b8a6', bg: '#ccfbf1', border: '#5eead4', text: '#115e59' },
  { name: 'slate',  dot: '#64748b', bg: '#e2e8f0', border: '#94a3b8', text: '#1e293b' },
];

const PALETTE_BY_NAME = Object.fromEntries(DOCTOR_PALETTE.map((c) => [c.name, c]));

function hash(str) {
  let h = 0;
  if (!str) return 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

// Build light/dark shades from a hex base color for custom colors
function shadesFromHex(hex) {
  const clean = hex.replace('#', '');
  const num = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const mix = (c, target, w) => Math.round(c * (1 - w) + target * w);
  const lighten = (w) => `rgb(${mix(r, 255, w)}, ${mix(g, 255, w)}, ${mix(b, 255, w)})`;
  const darken = (w) => `rgb(${mix(r, 0, w)}, ${mix(g, 0, w)}, ${mix(b, 0, w)})`;
  return {
    name: 'custom',
    dot: `rgb(${r}, ${g}, ${b})`,
    bg: lighten(0.78),
    border: lighten(0.55),
    text: darken(0.30),
  };
}

export function colorFromTag(tag) {
  if (!tag) return null;
  const t = String(tag).trim();
  if (t.startsWith('#')) {
    try { return shadesFromHex(t); } catch { return null; }
  }
  return PALETTE_BY_NAME[t.toLowerCase()] ?? null;
}

/**
 * Resolve a color object for a given input.
 * Accepts a string (userId or palette name / hex) or an object with `colorTag` + `id`.
 */
export function colorForDoctor(input) {
  if (!input) return DOCTOR_PALETTE[DOCTOR_PALETTE.length - 1];

  if (typeof input === 'object') {
    if (input.colorTag) return colorFromTag(input.colorTag) ?? colorForDoctor(input.id);
    return colorForDoctor(input.id);
  }

  const tagged = colorFromTag(input);
  if (tagged) return tagged;
  return DOCTOR_PALETTE[hash(String(input)) % DOCTOR_PALETTE.length];
}

export const DEFAULT_COLOR_NAME = 'sky';

/**
 * Format a doctor's display name with a "Dr." prefix without duplicating it.
 * "Ekta Dabas" → "Dr. Ekta Dabas"
 * "Dr. Ekta Dabas" → "Dr. Ekta Dabas" (no change)
 * "dr Ekta" → "Dr. Ekta"
 */
export function doctorDisplayName(fullName) {
  if (!fullName) return '—';
  const cleaned = String(fullName).trim().replace(/^dr\.?\s+/i, '');
  return `Dr. ${cleaned}`;
}

